import React, { useId } from 'react'
import clsx from 'clsx'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import './ApplyV2InputRate.scss'

interface Props {
    value?: number | null
    label?: string
    onChange?: (value: number) => void
    onFocus?: () => void
    onBlur?: React.FocusEventHandler<HTMLButtonElement>
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | null | undefined | boolean
    isRequired?: boolean
    isShowErrorMessage?: boolean
    maxRating?: number
    description?: React.ReactNode
}

const baseClass = 'ApplyV2InputRate'

function ApplyV2InputRate({
    value = 0,
    label,
    onChange,
    onFocus,
    onBlur,
    isDisabled = false,
    className = '',
    id,
    name,
    isLoading = false,
    errorMessage,
    isRequired = false,
    isShowErrorMessage = true,
    maxRating = 5,
    description,
}: Props) {
    const defaultId = useId()

    const handleRateChange = (newValue: number) => {
        if (!isDisabled && !isLoading && onChange) {
            onChange(newValue)
        }
    }
    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__input-wrapper`, {
                    [`${className}__input-wrapper`]: className,
                    [`${baseClass}__input-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__input-wrapper--loading`]: isLoading,
                    [`${baseClass}__input-wrapper--error`]: !!errorMessage,
                    [`${className}__input-wrapper--disabled`]: className && isDisabled,
                    [`${className}__input-wrapper--loading`]: className && isLoading,
                    [`${className}__input-wrapper--error`]: className && !!errorMessage,
                })}
            >
                <div className={`${baseClass}__stars`}>
                    {[...Array(maxRating)].map((_, index) => {
                        const starValue = index + 1
                        return (
                            <button
                                key={starValue}
                                name={name}
                                type="button"
                                className={clsx(`${baseClass}__star`, {
                                    [`${baseClass}__star--active`]: starValue <= (value || 0),
                                    [`${baseClass}__star--disabled`]: isDisabled,
                                    [`${baseClass}__star--error`]: !!errorMessage,
                                })}
                                onClick={() => handleRateChange(starValue)}
                                onFocus={onFocus}
                                onBlur={onBlur}
                                disabled={isDisabled || isLoading}
                                aria-label={`Rate ${starValue} out of ${maxRating}`}
                            >
                                {starValue <= (value || 0) ? '★' : '☆'}
                            </button>
                        )
                    })}
                </div>
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <WarningErrorIcon />
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2InputRate
